/**
 * Stale-build detection: reload a tab still running the bundle from before a deploy.
 *
 * The SPA never reloads index.html during in-app navigation, so a tab opened before a deploy
 * keeps running the old bundle against the new API. Where a response changed shape that crashes
 * (observed on /predicted-jeff-bids after 6.6.0: the old page read a field the new API no longer
 * sends, and React Router replaced the whole app with "Unexpected Application Error!").
 *
 * Each production build carries an id (`__BUILD_ID__`, injected by vite.config.ts) and writes the
 * same id to /version.json. The router-level VersionGuard fetches that file and, when the server's
 * id differs from the running one, reloads onto the new build. The decisions live here, the fetch
 * and the reload in ./buildCheck, so each can be unit-tested directly.
 */

// Replaced textually by Vite's `define` at build and dev time; never defined under Jest.
declare const __BUILD_ID__: string | undefined;

/** This bundle's build id, or '' where none was injected (Jest), which disables the check. */
export const RUNNING_BUILD_ID: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : '';

/** sessionStorage key holding the epoch-ms timestamp of the last new-build reload. */
export const BUILD_RELOAD_KEY = 'buildReloadAt';

/** Minimum gap between new-build reloads, guarding against reload loops. */
export const BUILD_RELOAD_MIN_INTERVAL_MS = 30_000;

/**
 * The build id in a /version.json body, or null when the body is not one.
 *
 * The Vite dev server has no version.json and answers the request with index.html, so
 * anything that is not `{ buildId: "<non-empty string>" }` means "no deployed build to compare".
 */
export function parseDeployedBuildId(body: unknown): string | null {
  const id = (body as { buildId?: unknown } | null)?.buildId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/** Whether the server holds a different build from the one this tab is running. */
export function isStaleBuild(running: string, deployed: string | null): boolean {
  return running !== '' && deployed !== null && deployed !== running;
}

/**
 * Whether to reload now, given when the last new-build reload happened.
 *
 * At most one reload per `BUILD_RELOAD_MIN_INTERVAL_MS`, so a server that keeps answering with
 * an id the reloaded page does not carry (a cache in front of index.html, say) cannot loop.
 *
 * @param lastReloadAt the stored timestamp string, or null if none recorded yet
 * @param now current time in epoch ms
 */
export function shouldReloadForNewBuild(lastReloadAt: string | null, now: number): boolean {
  if (!lastReloadAt) {
    return true;
  }
  const last = parseInt(lastReloadAt, 10);
  if (Number.isNaN(last)) {
    return true;
  }
  return now - last >= BUILD_RELOAD_MIN_INTERVAL_MS;
}

/** Read the last new-build reload time; null when none, or when sessionStorage is unavailable. */
export function readLastBuildReload(): string | null {
  try {
    return sessionStorage.getItem(BUILD_RELOAD_KEY);
  } catch {
    return null;
  }
}

/** Record a new-build reload; silently skipped when sessionStorage is unavailable. */
export function recordBuildReload(now: number): void {
  try {
    sessionStorage.setItem(BUILD_RELOAD_KEY, String(now));
  } catch {
    /* sessionStorage unavailable — the reload still happens, unthrottled */
  }
}
